import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { fmtShort } from "@/lib/datetime";
import { toast } from "sonner";

function elapsed(fromIso) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(fromIso).getTime()) / 60000));
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function TimeClockWidget() {
  const [open, setOpen] = useState(null);
  const [weekHours, setWeekHours] = useState(0);
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  const refresh = async () => {
    try {
      const [s, e] = await Promise.all([api.get("/timeclock/status"), api.get("/timeclock/entries")]);
      setOpen(s.data.open || null);
      const mine = (e.data.entries || []).filter((x) => x.hours != null);
      setWeekHours(mine.reduce((sum, x) => sum + Number(x.hours || 0), 0));
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { refresh(); const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  const punch = async (dir) => {
    setBusy(true);
    try {
      await api.post(`/timeclock/${dir}`);
      toast.success(dir === "clock-in" ? "Clocked in — shift started" : "Clocked out — hours recorded");
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Time clock failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-[#131B2A] border-[#28364F] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="timeclock-widget">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${open ? "bg-emerald-500/15 text-emerald-400" : "bg-[#0B0F17] text-[#64748B] border border-[#28364F]"}`}>
          <Clock className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm font-bold" data-testid="timeclock-status">
            {open ? <span className="text-emerald-400">On shift · {elapsed(open.clock_in)}</span> : <span className="text-[#94A3B8]">Off the clock</span>}
          </div>
          <div className="text-[11px] text-[#64748B] font-mono">
            {open ? `Clocked in ${fmtShort(open.clock_in)}` : "Not clocked in"} · This week: <span className="text-amber-300" data-testid="timeclock-week-hours">{weekHours.toFixed(2)}h</span>
          </div>
        </div>
      </div>
      {open ? (
        <Button onClick={() => punch("clock-out")} disabled={busy} className="bg-red-500 hover:bg-red-400 text-white font-bold" data-testid="clock-out-btn">
          <LogOut className="w-4 h-4 mr-1.5" /> Clock Out
        </Button>
      ) : (
        <Button onClick={() => punch("clock-in")} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" data-testid="clock-in-btn">
          <LogIn className="w-4 h-4 mr-1.5" /> Clock In
        </Button>
      )}
    </Card>
  );
}
